// config/api.js
import { getCurrentTimestamp } from '../utils/dateUtils';

// Environment detection (browser-only)
const getEnvironment = () => {
  if (typeof window === 'undefined') return 'server';

  const hostname = window.location.hostname;

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'development';
  }

  if (hostname === 'dinitracker.com' || hostname === 'dinitracker-v2-prod.pages.dev') {
    return 'production';
  }

  if (hostname === 'dinitracker-v2.pages.dev') {
    return 'staging';
  }

  console.warn('Unknown domain, defaulting to staging:', hostname);
  return 'staging';
};

const environment = getEnvironment();
const isDevelopment = environment === 'development';
const isProduction = environment === 'production';
const isStaging = environment === 'staging';

// API Configuration
const API_CONFIG = {
  API_URL: (() => {
    switch (environment) {
      case 'development':
        return 'http://localhost:5001';
      case 'production':
        return 'https://diniwigtracker-api.super-bread-aa95.workers.dev';
      case 'staging':
        return 'https://diniwigtracker-api.super-bread-aa95.workers.dev/staging';
      default:
        return 'https://diniwigtracker-api.super-bread-aa95.workers.dev/staging';
    }
  })(),

  // Media presign must always go to the CF Worker (it holds R2 credentials).
  // The local dev backend has no presign route, so we always target the Worker.
  MEDIA_PRESIGN_URL: environment === 'production'
    ? 'https://diniwigtracker-api.super-bread-aa95.workers.dev'
    : 'https://diniwigtracker-api.super-bread-aa95.workers.dev/staging',


  ENVIRONMENT: environment,

  GOOGLE_SHEETS_URL: process.env.REACT_APP_GOOGLE_SHEETS_URL || 'https://script.google.com/macros/s/AKfycby18IqS9MUEZe8gyN0e6EA9vK9ZCghezfFUldU9JQK-4q4MMyBep4MbRBvN4exBV9vKmg/exec',

  TIMEOUT: 30000,

  ENDPOINTS: {
    LOGIN: '/api/login',
    LOGOUT: '/api/logout',
    VERIFY_TOKEN: '/api/verify-token',
    ORDERS: '/api/orders',
    CUSTOMERS: '/api/customers',
    ROOMS: '/api/get-rooms',
    ACCESSORIES: '/api/get-accessories',
    REPAIR_OPTIONS: '/api/get-repair-options',
    CUSTOM_SURCHARGE: '/api/get-custom-surcharge',
    CUSTOMER_SEARCH: '/api/customers/search',
    HEALTH: '/health'
  }
};

export const API_URL = API_CONFIG.API_URL;
export const MEDIA_PRESIGN_URL = API_CONFIG.MEDIA_PRESIGN_URL;
export const GOOGLE_SHEETS_URL = API_CONFIG.GOOGLE_SHEETS_URL;

export const getApiUrl = (endpoint = '') => {
  return `${API_CONFIG.API_URL}${endpoint}`;
};

export const getEnvironmentInfo = () => ({
  environment: API_CONFIG.ENVIRONMENT,
  apiUrl: API_CONFIG.API_URL,
  isLocal: isDevelopment,
  isProduction: isProduction,
  isStaging: isStaging,
  hostname: typeof window !== 'undefined' ? window.location.hostname : 'server',
  timestamp: getCurrentTimestamp()
});

export const buildUrl = (endpoint) => {
  return getApiUrl(endpoint);
};

export const API_URLS = {
  HEALTH: buildUrl(API_CONFIG.ENDPOINTS.HEALTH),
  LOGIN: buildUrl(API_CONFIG.ENDPOINTS.LOGIN),
  LOGOUT: buildUrl(API_CONFIG.ENDPOINTS.LOGOUT),
  VERIFY_TOKEN: buildUrl(API_CONFIG.ENDPOINTS.VERIFY_TOKEN),
  ORDERS: buildUrl(API_CONFIG.ENDPOINTS.ORDERS),
  CUSTOMERS: buildUrl(API_CONFIG.ENDPOINTS.CUSTOMERS),
  ACCESSORIES: buildUrl(API_CONFIG.ENDPOINTS.ACCESSORIES),
  REPAIR_OPTIONS: buildUrl(API_CONFIG.ENDPOINTS.REPAIR_OPTIONS),
  CUSTOM_SURCHARGE: buildUrl(API_CONFIG.ENDPOINTS.CUSTOM_SURCHARGE),
  CUSTOMER_SEARCH: buildUrl(API_CONFIG.ENDPOINTS.CUSTOMER_SEARCH),
  ROOMS: (location) => buildUrl(`${API_CONFIG.ENDPOINTS.ROOMS}/${location}`),
};

export const apiRequest = async (url, options = {}) => {
  const defaultOptions = {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  };

  console.log(`🌐 API Request [${API_CONFIG.ENVIRONMENT}]:`, url);

  try {
    const response = await fetch(url, defaultOptions);

    const backendEnvironment = response.headers.get('X-Environment');
    const cacheStatus = response.headers.get('X-Cache');

    if (backendEnvironment) console.log(`🎯 Backend Environment: ${backendEnvironment}`);
    if (cacheStatus) console.log(`💾 Cache Status: ${cacheStatus}`);

    if (!response.ok) {
      console.error(`❌ API Error [${response.status}]:`, url);
    } else {
      console.log(`✅ API Success [${response.status}]:`, url);
    }

    return response;
  } catch (error) {
    console.error(`💥 API request failed [${API_CONFIG.ENVIRONMENT}]:`, error);
    throw error;
  }
};

export const apiCall = async (endpoint, options = {}) => {
  const url = buildUrl(endpoint);
  const response = await apiRequest(url, options);

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`API call failed: ${response.status} ${response.statusText} - ${errorText}`);
  }

  return response.json();
};

export { API_CONFIG };

if (typeof window !== 'undefined') {
  console.log('🚀 API Configuration Loaded:', {
    environment: API_CONFIG.ENVIRONMENT,
    apiUrl: API_CONFIG.API_URL,
    hostname: window.location.hostname,
    timestamp: getCurrentTimestamp()
  });
}

export default API_CONFIG;
